import type {
  AppSentEvent,
  CardContent,
  DeltaVerdict,
  MedScreenContent,
  MorningCheckChoice,
  ObsDayContent,
  PracticeContent,
  SafetyScreeningContent,
  ScreenContent,
} from '@lma/shared';
import { getInitData, getTimezone } from './telegram';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000';

export interface Profile {
  id: string;
  firstName: string | null;
  lang: string;
  timezone: string;
  source: string | null;
  createdAt: string;
  termsAccepted: boolean;
  safetyStatus: 'unknown' | 'clear' | 'stop_medical';
  safetyChangedAt: string | null;
  currentCardKey: string | null;
  flowStatus: string;
}

export interface Session {
  token: string;
  isNew: boolean;
  profile: Profile;
  entryScreen: string;
}

export interface Resolved<T> {
  key: string;
  type: string;
  revisionId: string;
  revisionNo: number;
  data: T;
}

export interface MeasureResult {
  before: number;
  after: number;
  delta: number;
  verdict: DeltaVerdict;
  label: string;
}

let token: string | null = null;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Куда уводить женщину. Решает сервер: например, на медицинский маршрут. */
    readonly redirect?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_URL}${path}`, { ...init, headers });
  const body: unknown = await response.json().catch(() => ({}));

  if (!response.ok) {
    const payload = body as { error?: string; message?: string; redirect?: string };
    throw new ApiError(
      response.status,
      payload.error ?? 'unknown',
      payload.message ?? 'Что-то пошло не так',
      payload.redirect,
    );
  }

  return body as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });

/** Обмен подписанной initData на токен приложения. Делается один раз при запуске. */
export async function authenticate(): Promise<Session> {
  const session = await post<Session>('/api/app/auth/telegram', {
    initData: getInitData(),
    timezone: getTimezone(),
  });
  token = session.token;
  return session;
}

/**
 * Вход без Telegram — только для разработки, чтобы смотреть экраны в браузере.
 * Сервер отвечает на этот маршрут лишь вне прода.
 */
export async function authenticateDev(): Promise<Session> {
  const session = await post<Session>('/api/app/auth/dev', { timezone: getTimezone() });
  token = session.token;
  return session;
}

// ─── Содержание ────────────────────────────────────────────
// Ничего из этого не зашито в код: тексты, признаки, шаги проб и подписи
// кнопок приходят из базы и меняются Еленой без релиза.

/**
 * Все экраны одним запросом.
 *
 * Список ключей на стороне приложения не живёт: он дважды разошёлся
 * с содержанием. Что существует — знает сервер.
 */
export const fetchAllScreens = () =>
  request<{ screens: Resolved<ScreenContent>[] }>('/api/app/content/screens/all');

export const fetchScreen = (key: string) =>
  request<Resolved<ScreenContent>>(`/api/app/content/screen:${key}`);

export const fetchCards = () =>
  request<{ cards: Resolved<CardContent>[] }>('/api/app/content/cards');

export const fetchCard = (key: string) => request<Resolved<CardContent>>(`/api/app/content/${key}`);

export const fetchMedScreen = (key: string) =>
  request<Resolved<MedScreenContent>>(`/api/app/content/${key}`);

export const fetchPractice = (key: string) =>
  request<Resolved<PracticeContent>>(`/api/app/content/${key}`);

export const fetchSafetyScreening = () =>
  request<Resolved<SafetyScreeningContent>>('/api/app/content/safety:screening');

// ─── Путь ──────────────────────────────────────────────────

/**
 * Шаги, о которых сервер иначе не узнает.
 *
 * Ошибку глотаем: пропавшее событие — испорченная строка в отчёте,
 * а упавший экран — испорченный день. Цена несопоставима.
 *
 * События копятся и уходят пачкой. Открытие экрана пишется на каждом
 * переходе, а их за сессию три десятка: столько же отдельных запросов
 * с телефона в метро — это заметные задержки на ровном месте.
 */
type EventProps = Record<string, string | number | boolean>;

interface QueuedEvent {
  name: AppSentEvent;
  props?: EventProps;
  at: string;
}

const queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Отправка пачки.
 *
 * `keepalive` держит запрос живым, когда женщина сворачивает приложение, —
 * иначе последний экран сессии, самый интересный для отчёта, теряется чаще
 * всех остальных.
 */
function flushEvents(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  // Токена ещё нет — не выбрасываем накопленное, а ждём. Иначе первые
  // события сессии тихо пропадают, и именно они самые интересные.
  if (!token) {
    flushTimer = setTimeout(flushEvents, 2000);
    return;
  }

  const batch = queue.splice(0, queue.length);
  void request<{ ok: true }>('/api/app/events', {
    method: 'POST',
    body: JSON.stringify({ events: batch }),
    keepalive: true,
  }).catch(() => undefined);
}

export function trackEvent(name: AppSentEvent, props?: EventProps): void {
  // Время ставим здесь: пачка уходит с задержкой, и без своей метки
  // все события сессии слиплись бы в одну секунду.
  queue.push({ name, props, at: new Date().toISOString() });

  if (queue.length >= 12) {
    flushEvents();
    return;
  }
  flushTimer ??= setTimeout(flushEvents, 2000);
}

if (typeof document !== 'undefined') {
  // Сворачивание — единственный надёжный момент «сессия закончилась».
  // События выгружаемся отдавать именно здесь, а не по `beforeunload`:
  // на телефоне его часто не случается вовсе.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEvents();
  });
}

/**
 * Вопрос своими словами.
 *
 * Имя и возраст женщина называет сама: именно их Елена произносит вслух,
 * разбирая вопрос. Всё остальное остаётся закрытым, и в приложении об этом
 * сказано прямо — иначе писать не станут.
 */
export const askQuestion = (input: {
  text: string;
  authorName: string;
  authorAge: number | null;
  screenKey?: string | null;
}) => post<{ ok: true }>('/api/app/products/question', input);

export const acceptTerms = () => post<{ next: string }>('/api/app/journey/terms');

export const submitScreening = (hasFlags: boolean) =>
  post<{ safetyStatus: string; next: string }>('/api/app/journey/screening', { hasFlags });

export const clearSafetyByUser = () =>
  post<{ safetyStatus: string; next: string }>('/api/app/journey/safety/cleared-by-me');

export const selectCard = (key: string) =>
  post<{ ok: true }>(`/api/app/journey/cards/${key}/select`);

export const measureBefore = (key: string, value: number) =>
  post<{ next: string }>(`/api/app/journey/cards/${key}/measure-before`, { value });

export const measureAfter = (
  key: string,
  value: number,
  extraChoice?: string,
  /** Ощущения своими словами: то, чего нет в шкале. */
  note?: string,
) =>
  post<{ result: MeasureResult }>(`/api/app/journey/cards/${key}/measure-after`, {
    value,
    extraChoice,
    note,
  });

/**
 * Наблюдение после результата.
 *
 * Отдельным запросом, а не полем замера: женщина пишет его, уже увидев,
 * что изменилось, — то есть когда замер давно ушёл на сервер.
 */
export const saveCardNote = (key: string, input: { text: string; cycleDay: number | null }) =>
  post<{ ok: true }>(`/api/app/journey/cards/${key}/note`, input);

export const reportWorse = (screenKey: string, cardKey?: string) =>
  post<{ next: string }>('/api/app/journey/worse', { screenKey, cardKey });

export const takeRoute = (key: string, target: 'intensive21' | 'lpf' | 'obs7') =>
  post<{ ok: true }>(`/api/app/journey/cards/${key}/route`, { target });

export const answerMorningCheck = (choice: MorningCheckChoice, cardKey?: string) =>
  post<{ ok: true }>('/api/app/journey/morning-check', { choice, cardKey });

// ─── Семь дней наблюдения ──────────────────────────────────

export interface ObservationRunState {
  id: string;
  status: 'active' | 'completed' | 'stopped_medical' | 'abandoned';
  currentDay: number;
  startedAt: string;
  days: { dayIndex: number; status: string }[];
  /** Сегодня уже пройдено или день ещё не открылся: форму показывать нельзя. */
  waitingForNextDay?: boolean;
  /** Когда возвращаться. */
  opensAt?: string | null;
  /** Признак серии, если он уже выбран (с дня 1). */
  marker?: ObservationMarker | null;
}

/** Признак серии: подпись группы и формулировка, которую она дала сама. */
export interface ObservationMarker {
  key: string;
  label: string;
  text: string;
}

/** Одна позиция графика: день, оценка беспокойства (null — пропуск) и её запись. */
export interface ObservationPoint {
  dayIndex: number;
  concern: number | null;
  note: string | null;
  status: string;
}

/** Карта серии «один признак». Сравнения и вердикты здесь больше не отдаются. */
export interface ObservationResult {
  runId: string;
  status: ObservationRunState['status'];
  /** Серия начата до обновления: признака нет, записи сохранены. */
  legacy: boolean;
  marker: ObservationMarker | null;
  points: ObservationPoint[];
  daysDone: number;
  /** Итог седьмого дня — её слова, дословно. */
  ownWords: string | null;
  /** Продукты показываем, только если не было красного флага и статус чистый. */
  productsOpen: boolean;
}

export const startObservation = (entryCardKey: string | null) =>
  post<{ runId: string; dayIndex: number }>('/api/app/observation/start', { entryCardKey });

export const fetchObservation = () =>
  request<{
    run: ObservationRunState | null;
    day: { key: string; revisionNo: number; data: ObsDayContent } | null;
  }>('/api/app/observation/current');

export const submitDay = (dayIndex: number, answers: unknown) =>
  post<{ completed: boolean }>(`/api/app/observation/days/${dayIndex}/done`, answers);

export const skipDay = (dayIndex: number) =>
  post<{ abandoned: boolean }>(`/api/app/observation/days/${dayIndex}/skip`);

export const reportDayWorse = (dayIndex: number) =>
  post<{ next: string }>(`/api/app/observation/days/${dayIndex}/worse`);

export const fetchObservationResult = () =>
  request<ObservationResult>('/api/app/observation/result');

export const fetchRouteOptions = () =>
  request<{ showProgram: boolean; hadFlag: boolean }>('/api/app/observation/route-options');

export const saveAnchor = (text: string, nextStep?: string) =>
  post<{ ok: true }>('/api/app/observation/anchor', { text, nextStep });

// ─── Продукты ──────────────────────────────────────────────

export const fetchPricing = () =>
  request<{
    products: {
      code: string;
      priceMinor: number;
      currency: string;
      isFree: boolean;
      isSellable: boolean;
      priceOverrideText: string | null;
    }[];
  }>('/api/app/products/pricing');

export const fetchLinks = () =>
  request<{ whatsapp: string | null; support: string | null }>('/api/app/products/links');

export const requestBooking = (city: string, contextCardKey: string | null) =>
  post<{ served: boolean; whatsappTemplate: string }>('/api/app/products/booking', {
    city,
    contextCardKey,
  });

/**
 * Предзапись на программу. До 26 октября сервер отвечает датой открытия и ничего не записывает.
 */
export const joinProgramWaitlist = () =>
  post<{ opensAt: string | null }>('/api/app/products/program-waitlist');

/** Заявка на онлайн-консультацию. Возвращает готовый текст для WhatsApp. Оплата приходит после проверки. */
export const consultRequest = (text = '') =>
  post<{ whatsappTemplate: string }>('/api/app/products/consult-request', { text });

export const requestDeletion = () => post<{ ok: true }>('/api/app/products/deletion/request');
export const confirmDeletion = () => post<{ deleted: true }>('/api/app/products/deletion/confirm');

export interface MyMeasurement {
  context: string;
  cardKey: string | null;
  scaleKey: string | null;
  value: number | null;
  choice: string | null;
  createdAt: string;
}

/** Заметка женщины своими словами. К ним она возвращается через месяц. */
export interface SelfNote {
  id: string;
  text: string;
  context: string;
  cardKey: string | null;
  createdAt: string;
}

export const fetchMyResult = () =>
  request<{
    currentCardKey: string | null;
    flowStatus: string;
    cardHistory: { cardKey: string; createdAt: string }[];
    measurements: MyMeasurement[];
    notes: SelfNote[];
    /**
     * База дня за те же даты, что и замеры: второй слой поверх первого.
     * Ключ — дата, YYYY-MM-DD.
     */
    dayBase: Record<string, { label: string; value: string }[]>;
  }>('/api/app/journey/my-result');

// ─── Клуб: подписка ────────────────────────────────────────

/**
 * Тарифы клуба.
 *
 * Сетка пересобрана под двенадцатинедельный цикл: месяц, два и три.
 * Полугодовой и годовой сняты с продажи — они остались в перечислении
 * на сервере ради уже выданных доступов, но выбрать их больше нельзя.
 */
export type ClubTariff = 'club_trial' | 'club_1' | 'club_2' | 'club_3';

export interface PaymentTerms {
  product: string;
  priceMinor: number;
  currency: string;
  isSellable: boolean;
  recurring: boolean;
  intervalMonths: number;
  /** Срок разового доступа в днях. У подписок его нет. */
  accessDays: number | null;
  nextChargeAt: string | null;
  cancelHint: string | null;
  cancelConsequence: string | null;
  includes: string[];
  alreadySubscribed: boolean;
}

export interface Subscription {
  id: string;
  product: string;
  status: 'active' | 'past_due' | 'cancelling' | 'ended';
  intervalMonths: number;
  priceMinor: number;
  currency: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

/** Условия списания до оплаты — пункт 5.9 оферты. Считает сервер. */
export const paymentTerms = (product: string) =>
  request<PaymentTerms>(`/api/app/payments/terms/${product}`);

/**
 * Начать оплату.
 *
 * Согласие на регулярное списание уходит вместе с запросом: пункт 5.8
 * оферты требует, чтобы оно было отдельным и однозначным, а согласие,
 * которое нельзя подтвердить, юридически не существует.
 */
export const startPayment = (product: string, recurringConsent?: boolean) =>
  post<{ purchaseId: string; confirmationUrl: string }>('/api/app/payments/start', {
    product,
    recurringConsent,
  });

export const mySubscriptions = () =>
  request<{ subscriptions: Subscription[] }>('/api/app/payments/my-subscriptions');

export const cancelSubscription = (id: string) =>
  post<{ endsAt: string }>(`/api/app/payments/subscriptions/${id}/cancel`);

// ─── Дом ───────────────────────────────────────────────────

export interface HomeData {
  firstName: string | null;
  safetyStatus: string;
  currentCard: { key: string | null; title: string } | null;
  observation: {
    status: string;
    done: number;
    total: number;
    nextDay: number | null;
    /** Сегодня уже сделано или день ещё не открылся. */
    waitingForNextDay: boolean;
    opensAt: string | null;
  } | null;
  result: {
    count: number;
    first: number | null;
    last: number | null;
    delta: number | null;
    recent: number[];
    lastMorning: string | null;
  };
  access: { intensive: boolean; club: boolean };
}

/** Всё для домашнего экрана одним запросом: на телефоне водопад запросов виден. */
export const fetchHome = () => request<HomeData>('/api/app/journey/home');

/** Свои доступы: что уже открыто и до какого числа. */
export const myAccess = () =>
  request<{ accesses: { product: string; grantedAt: string; expiresAt: string | null }[] }>(
    '/api/app/payments/my-access',
  );

/**
 * Ссылка-приглашение в закрытый канал.
 *
 * Пусто — значит доступа нет. Решает сервер: ссылка вида t.me/+… пускает
 * любого, кто её увидел.
 */
export const channelLink = (product: string) =>
  request<{ link: string | null }>(`/api/app/products/channel/${product}`);

// ─── База дня ──────────────────────────────────────────────

/**
 * Второй слой продукта: каким был день, а не только что случилось с лицом.
 * Одна запись на день, она переписывается — женщина отмечает утром сон,
 * а вечером возвращается и ставит напряжение.
 */
export interface DayEntry {
  sleep: string | null;
  movement: string | null;
  food: string | null;
  tension: string | null;
  recovery: string | null;
  wellbeing: number | null;
  cycleState: string | null;
  cycleDay: number | null;
  note: string | null;
}

export interface DayOfWeek extends DayEntry {
  date: string;
  weekday: string;
  filled: boolean;
}

export const fetchToday = () =>
  request<{ localDate: string; entry: DayEntry | null }>('/api/app/day');

export const saveDay = (entry: DayEntry) => post<{ ok: true }>('/api/app/day', entry);

export const fetchDayWeek = () => request<{ days: DayOfWeek[] }>('/api/app/day/week');

export const fetchDayCalendar = (from: string, to: string) =>
  request<{
    from: string;
    to: string;
    earliest: string | null;
    days: (DayEntry & { date: string })[];
  }>(`/api/app/day/calendar?from=${from}&to=${to}`);
